import { isFuture, isPast, parseISO } from 'date-fns';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Breakpoints, Grid, Type } from '../../../app/StyleGuide';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { OutlineButton, PrimaryButton } from '../../../components/Buttons';
import { CurriculumBrandBanner } from '../../../components/CurriculumBrandBanner';
import ErrorMessage from '../../../components/ErrorMessage';
import { FileUploader } from '../../../components/fileUploader';
import { FormField, FormFieldContainer, FormGroupContainer, Label, Radio } from '../../../components/FormElements';
import { Container, PageTitle } from '../../../components/Layout';
import LoadingState from '../../../components/LoadingState';
import useCurriculum from '../../../hooks/useCurriculum';
import alertService from '../../../services/AlertService';
import curriculumService, { CurriculumStorageLocations } from '../../../services/curriculumService';
import windowService from '../../../services/windowService';
import { handleError } from '../../../utils/apiUtils';
import { getFileTypeByMimeType } from '../../../utils/fileUtils';
import { useUser } from '../../authentication';

const PageContainer = styled(Container)`
  margin: ${Grid._6} auto ${Grid._10} auto;
`;

const CollectionRadioContainer = styled.div`
  display: flex;
  gap: ${Grid._5};
  margin-bottom: ${Grid._4};
  align-items: center;
  & > label {
    display: flex;
    flex-direction: column;
    gap: ${Grid._2};
    & > h1 {
      font-size: ${Type.Scale._3};
      margin: 0px;
      font-weight: ${Type.Weight.semibold};
    }

    & > h2 {
      font-size: ${Type.Scale._2};
      margin: 0px;
      font-weight: ${Type.Weight.normal};
      color: ${Type.Color.medium};
    }
  }
`;

const CollectionRadio = styled(Radio)`
  width: 27px;
  height: 27px;
  min-width: 27px;

  &::before {
    width: 29px;
    height: 29px;
  }
  &::after {
    width: 29px;
    height: 29px;
  }
`;

const MaterialFormSectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: ${Grid._8};
  gap: ${Grid._7};
  margin-bottom: ${Grid._10};
  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    flex-direction: column;
    gap: 0;
    margin-bottom: ${Grid._4};
  }

  @media screen and (max-width: ${Breakpoints.screen_xs}) {
    margin-top: ${Grid._5};
    margin-bottom: 0;
  }
`;

const MaterialFormSection = styled.div<{ flex?: number }>`
  flex: ${props => (props.flex ? props.flex : '1')};
`;

const MaterialFormButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${Grid._6};
  @media (max-width: ${Breakpoints.screen_xs}) {
    flex-direction: column-reverse;
    gap: ${Grid._4};
    width: 100%;
  }
`;

const FileTypeToMaterialType = Object.freeze({
  audio: 'audio',
  video: 'video',
  image: 'download',
  document: 'download',
  epub: 'download',
  zip: 'download',
});

type Collection = {
  collectionId: string;
  curriculumId: string;
  image: string;
  itemNumber: string;
  name: string;
  order: number;
  package: string;
  seriesId: string;
  endDate?: string;
};

type Session = {
  curriculumId: string;
  issueId: string;
  issueName: string;
  name: string;
  publishEndDate: string;
  publishStartDate: string;
  recommendedDate?: string;
  scheduledDate?: string;
  sessionId: string;
  sessionNumber: number;
  useDate: string;
};

type SessionMaterial = {
  description: string;
  id: string;
  itemNumber: string;
  materialId: string;
  materialType: string;
  name: string;
  order: number;
  package: string;
  seriesId: string[];
};

function CollectionItem({
  checked,
  packageName,
  name,
  onClick,
}: {
  checked: boolean;
  packageName: string;
  name: string;
  onClick: () => void;
}) {
  return (
    <CollectionRadioContainer>
      <CollectionRadio
        id={`materialCollectionRadio ${name}`}
        data-qa-hook={`materialCollectionRadio`}
        checked={checked}
        onClick={onClick}
        onChange={() => {}}
      />
      <label htmlFor={`materialCollectionRadio ${name}`}>
        <h1>{name}</h1>
        <h2>{packageName === 'Basic' ? 'All Plans' : 'Plus Only'}</h2>
      </label>
    </CollectionRadioContainer>
  );
}

const ManageCurriculumMaterial = () => {
  const { curriculumId, unitId, sessionId, materialId } = useParams<{
    curriculumId: string;
    unitId: string;
    sessionId: string;
    materialId: string;
  }>();
  const { user } = useUser();
  const { brand, ageCategory } = useCurriculum();
  const [data, setData] = useState<{
    isLoading: boolean;
    isError: boolean;
    session: Session | null;
    unit: any;
    material: any;
    sessionMaterials: SessionMaterial[];
    collections: Collection[];
  }>({
    isLoading: true,
    isError: false,
    session: null,
    unit: {},
    material: {},
    sessionMaterials: [],
    collections: [],
  });
  const { session, isLoading, isError, material, collections, sessionMaterials } = data;

  const activeCollections = collections?.filter(item => !item.endDate || isFuture(parseISO(item.endDate)));
  const expiredCollections = collections?.filter(item => item.endDate && isPast(parseISO(item.endDate)));

  const [crumbs, setCrumbs] = useState([
    { name: 'Organization', route: '#/org' },
    { name: 'Manage Curriculum', route: '#/manage-curriculum' },
    {
      name: `${brand.name}: ${ageCategory}`,
      route: `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}`,
    },
  ]);

  const determineUploadLocation = ({ type }: { type: string }) => {
    const fileType = getFileTypeByMimeType(type);

    switch (fileType) {
      case 'audio':
      case 'video':
      case 'image':
        return CurriculumStorageLocations[fileType.toUpperCase() as keyof typeof CurriculumStorageLocations];
      default:
        return CurriculumStorageLocations.FILE;
    }
  };

  useEffect(() => {
    Promise.all([
      curriculumService.getIndividualUnit(unitId, curriculumId),
      curriculumService.getSession(unitId, sessionId),
      curriculumService.getCurriculumCollections(curriculumId),
      curriculumService.getSessionMaterials(sessionId),
    ])
      .then(([unitData, sessionData, collectionsData, sessionMaterialsData]) => {
        setData(prevData => ({
          ...prevData,
          unit: unitData,
          session: sessionData,
          collections: collectionsData,
          sessionMaterials: sessionMaterialsData,
        }));

        setCrumbs(prevCrumbs => [
          ...prevCrumbs,
          {
            name: `${unitData.name}`,
            route: `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}/unit/${unitId}`,
          },
          {
            name: `${sessionData.name}`,
            route: `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}/unit/${unitId}/session/${sessionId}/settings`,
          },
          {
            name: materialId ? `Edit Material` : 'New Material',
            route: '',
          },
        ]);

        if (materialId) {
          return curriculumService.getSessionMaterial(sessionId, materialId);
        }

        return null;
      })
      .then(materialData => {
        if (materialData) {
          setData(prevData => ({
            ...prevData,
            material: materialData,
          }));
        }

        setData(prevData => ({ ...prevData, isLoading: false, isError: false }));
      })
      .catch(error => {
        setData(prevData => ({ ...prevData, isError: true, isLoading: false }));
        handleError(error);
      });
  }, []);

  const getMaxMaterialOrder = () => {
    const maxOrder = sessionMaterials.reduce((acc, curr) => {
      if (curr.order > acc) {
        return curr.order;
      }
      return acc;
    }, 0);
    return maxOrder;
  };

  const uploadMaterialFile = (mediaId: string, materialType: string, fileName: string) =>
    materialType === 'video' || materialType === 'audio'
      ? curriculumService.uploadSessionMaterialMedia(mediaId, curriculumId, fileName, user?.userId)
      : curriculumService.uploadSessionMaterialFile(mediaId, curriculumId, fileName, user?.userId);

  const updateMaterial = ({
    name,
    collections,
    package: pkg,
  }: {
    name: string;
    collections: string[];
    package: string;
  }) => {
    const updateMaterialPromise =
      material.package !== pkg || material.seriesIds !== collections
        ? curriculumService.updateSessionMaterial(sessionId, materialId, collections, pkg)
        : Promise.resolve();

    return updateMaterialPromise
      .then(() => {
        if (material.name !== name) {
          return curriculumService.updateSessionMaterialName(sessionId, materialId, name).then(() => {});
        }
      })
      .then(() => {
        alertService.showOnNextPage('Material Saved');
        windowService.redirectTo(
          `/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}/unit/${unitId}/session/${sessionId}/settings`
        );
      })
      .catch(error => handleError(error));
  };

  const handleSubmit = (
    values: {
      name: string;
      package: string;
      file: { type: string; name: string; id: string };
      collections: string[];
    },
    { setFieldError }: { setFieldError: (field: string, message?: string) => void }
  ) => {
    const { name, collections, file, package: pkg } = values;

    if (materialId) return updateMaterial(values);

    const fileTypeKey = getFileTypeByMimeType(file.type);
    const materialType = fileTypeKey
      ? FileTypeToMaterialType[fileTypeKey as keyof typeof FileTypeToMaterialType]
      : null; // Ensure fileTypeKey is defined
    if (!materialType) {
      setFieldError('file', 'File type is invalid');
      console.error('Unsupported material type from file type'); // eslint-disable-line no-console
      return Promise.reject();
    }

    const { id: mediaId, name: fileName } = file;
    const nextOrder = getMaxMaterialOrder() + 1;

    return curriculumService
      .createSessionMaterial(sessionId, {
        materialType,
        mediaId,
        name,
        order: nextOrder,
        package: pkg,
        seriesIds: collections,
      })
      .then(() => uploadMaterialFile(mediaId, materialType, fileName))
      .then(() => {
        alertService.showOnNextPage('Material Added');
        windowService.redirectTo(
          `/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}/unit/${unitId}/session/${sessionId}/settings`
        );
      })
      .catch(error => handleError(error));
  };

  const handleCollectionToggle = (
    collectionId: string,
    setFieldValue: (field: string, value: any) => void,
    values: { collections: string[] }
  ) => {
    if (values.collections.includes(collectionId)) {
      setFieldValue(
        'collections',
        values.collections.filter(id => id !== collectionId)
      );
    } else {
      setFieldValue('collections', [...values.collections, collectionId]);
    }
  };

  const getInitialFormValue = (material: any | null) => {
    return material
      ? {
          ...material,
          file: '',
          collections: material.seriesIds,
        }
      : {
          name: '',
          package: 'Basic',
          file: '',
          collections: [],
        };
  };

  const handleCancel = () => {
    windowService.redirectTo(
      `#/manage-curriculum/${brand.code}/${ageCategory}/${curriculumId}/unit/${unitId}/session/${sessionId}/settings`
    );
  };

  return (
    <>
      <Breadcrumbs crumbs={crumbs}></Breadcrumbs>
      <CurriculumBrandBanner brandCode={brand.code} ageCategory={ageCategory} />
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorMessage />
      ) : (
        <PageContainer>
          <PageTitle>
            <h1>{session?.name}</h1>
          </PageTitle>

          <Formik
            initialValues={getInitialFormValue(materialId && material)}
            validationSchema={Yup.object({
              name: Yup.string().max(64, 'Name is too long').required('A name is required'),
              file: Yup.mixed().when('materialId', {
                is: (materialId: string) => Boolean(!materialId),
                then: Yup.mixed().required('A file is required'),
                otherwise: Yup.mixed(),
              }),
              collections: Yup.array().min(1, 'At least one collection is required'),
            })}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, isSubmitting, errors, submitCount }) => {
              return (
                <Form>
                  <MaterialFormSectionWrapper>
                    <MaterialFormSection flex={7}>
                      <FormField
                        label="Name"
                        data-qa-hook="materialName"
                        name="name"
                        placeholder="Enter Material Name"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('name', e.target.value)}
                      />
                      <FormFieldContainer>
                        <Label>Plans</Label>
                        <FormGroupContainer>
                          <p>Select which plans will have access to this material</p>
                          <div className="controls">
                            <Radio
                              id="material-package-availability--basic"
                              data-qa-hook="materialPackageAvailabilityPlusBasic"
                              checked={values.package === 'Basic'}
                              onChange={() => setFieldValue('package', 'Basic')}
                            />
                            <label htmlFor="material-package-availability--basic">All Plans</label>
                            <Radio
                              id="material-package-availability--plus"
                              data-qa-hook="materialPackageAvailabilityPlus"
                              checked={values.package === 'Plus'}
                              onChange={() => setFieldValue('package', 'Plus')}
                            />
                            <label htmlFor="material-plan-availability--plus">Plus Only</label>
                          </div>
                        </FormGroupContainer>
                      </FormFieldContainer>
                      {!materialId && user && (
                        <FormFieldContainer>
                          <Label>Upload File</Label>
                          <FileUploader
                            orgId={user.lastSelectedAccount}
                            onFileUploaded={file => setFieldValue('file', file)}
                            onFileRemoved={() => setFieldValue('file', null)}
                            uploadLocation={determineUploadLocation}
                          />
                          {submitCount > 0 && errors.file && <ErrorMessage>{errors.file}</ErrorMessage>}
                        </FormFieldContainer>
                      )}
                    </MaterialFormSection>
                    <MaterialFormSection flex={6}>
                      <FormFieldContainer>
                        <Label>Active Collections</Label>
                        {activeCollections.length > 0 ? (
                          activeCollections.map(({ name, package: packageName, collectionId }) => (
                            <CollectionItem
                              name={name}
                              packageName={packageName}
                              checked={values?.collections && values.collections.includes(collectionId)}
                              key={collectionId}
                              onClick={() => handleCollectionToggle(collectionId, setFieldValue, values)}
                            />
                          ))
                        ) : (
                          <p>No active collections</p>
                        )}
                        {submitCount > 0 && errors.collections && <ErrorMessage>{errors.collections}</ErrorMessage>}
                      </FormFieldContainer>
                      {expiredCollections.length > 0 && (
                        <FormFieldContainer>
                          <Label>Expired Collections</Label>
                          {expiredCollections.map(({ name, package: packageName, collectionId }) => (
                            <CollectionItem
                              name={name}
                              packageName={packageName}
                              checked={values?.collections && values.collections.includes(collectionId)}
                              key={collectionId}
                              onClick={() => handleCollectionToggle(collectionId, setFieldValue, values)}
                            />
                          ))}
                        </FormFieldContainer>
                      )}
                    </MaterialFormSection>
                  </MaterialFormSectionWrapper>
                  <MaterialFormButtonContainer>
                    <OutlineButton type="button" onClick={() => handleCancel()}>
                      Cancel
                    </OutlineButton>
                    <PrimaryButton
                      type="submit"
                      disabled={isSubmitting}
                      operating={isSubmitting}
                      data-qa-hook="materialSave"
                    >
                      {`${materialId ? 'Save' : 'Add'} Material`}
                    </PrimaryButton>
                  </MaterialFormButtonContainer>
                </Form>
              );
            }}
          </Formik>
        </PageContainer>
      )}
    </>
  );
};

export default ManageCurriculumMaterial;
